import * as React from "react";
import { useEffect, useState } from "react";
import axios from "axios";
import { useHistory } from "react-router-dom";
import { Button, CircularProgress, TableCell, TableRow, TextField } from "@material-ui/core";
import { Notification, useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet } from "../getConfig";
import { BrandPicker, brandLabel, useBrandNames } from "./brands";
import {
  DataTable,
  FilterSelect,
  PAGE_SIZE,
  PageHeader,
  ServerPagination,
  StatusChip,
  errorMessage,
  fmtDateTime,
  gbp,
  plainOptions,
  useCloStyles,
} from "./shared";

export const TXN_STATUSES = ["matched", "unmatched", "ignored", "expired"];

export const TXN_REASONS = [
  "unknown_user",
  "unknown_offer",
  "ambiguous_offer",
  "unknown_merchant",
  "currency_not_gbp",
  "orphan_refund",
  "card_unmatched",
  "already_settled",
];

export const NETWORKS = ["visa", "mastercard"];

export const EVENT_TYPES = ["authorisation", "settlement", "refund", "reversal", "chargeback"];

const COLUMNS = [
  { label: "ID" },
  { label: "Transaction at" },
  { label: "Event" },
  { label: "Status" },
  { label: "Reason" },
  { label: "Merchant" },
  { label: "Amount", numeric: true },
  { label: "Brand" },
  { label: "User ID" },
  { label: "ARN" },
];

type Filters = {
  status: string;
  reason: string;
  network: string;
  eventType: string;
  brandId: string;
  userId: string;
  arn: string;
};

const EMPTY_FILTERS: Filters = {
  status: "",
  reason: "",
  network: "",
  eventType: "",
  brandId: "",
  userId: "",
  arn: "",
};

const TransactionsList = () => {
  const classes = useCloStyles();
  const notify = useNotify();
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const names = useBrandNames(rows.map((r) => r.brand_id));

  // Overrides exist because setState has not applied yet when Clear re-fetches.
  const fetchRows = async (nextPage = 1, overrides?: Filters) => {
    const active = overrides || filters;
    const params: any = { page: nextPage, pageSize: PAGE_SIZE };
    if (active.status) params.status = active.status;
    if (active.reason) params.reason = active.reason;
    if (active.network) params.network = active.network;
    if (active.eventType) params.event_type = active.eventType;
    if (active.brandId) params.brand_id = active.brandId;
    if (active.userId.trim()) params.user_id = active.userId.trim();
    if (active.arn.trim()) params.arn = active.arn.trim();
    try {
      setLoading(true);
      const res: any = await axios.get(ENDPOINTS.CLO_TRANSACTIONS, getConfigGet(params));
      setRows(res.data?.data || []);
      setTotal(res.data?.total || 0);
      setPage(nextPage);
    } catch (err) {
      notify(errorMessage(err, "Failed to load transactions"), "warning");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const clear = () => {
    setFilters(EMPTY_FILTERS);
    fetchRows(1, EMPTY_FILTERS);
  };

  const setFilter = (key: keyof Filters) => (value: string) => setFilters({ ...filters, [key]: value });

  const onEnter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") fetchRows(1);
  };

  return (
    <div className={classes.main}>
      <PageHeader title="Transactions" subtitle="Card network events and how each was matched" />

      <div className={classes.filterContainer}>
        <FilterSelect label="Status" value={filters.status} onChange={setFilter("status")} options={plainOptions(TXN_STATUSES)} />
        <FilterSelect label="Reason" value={filters.reason} onChange={setFilter("reason")} options={plainOptions(TXN_REASONS)} />
        <FilterSelect label="Network" value={filters.network} onChange={setFilter("network")} options={plainOptions(NETWORKS)} />
        <FilterSelect label="Event type" value={filters.eventType} onChange={setFilter("eventType")} options={plainOptions(EVENT_TYPES)} />
        <BrandPicker className={classes.filterField} value={filters.brandId} onChange={setFilter("brandId")} />
        <TextField
          variant="outlined"
          size="small"
          label="User ID"
          className={classes.filterField}
          value={filters.userId}
          onChange={(e) => setFilter("userId")(e.target.value)}
          onKeyPress={onEnter}
        />
        <TextField
          variant="outlined"
          size="small"
          label="ARN"
          className={classes.filterField}
          value={filters.arn}
          onChange={(e) => setFilter("arn")(e.target.value)}
          onKeyPress={onEnter}
        />
        <Button className={classes.filterButton} variant="contained" color="primary" disableElevation onClick={() => fetchRows(1)} disabled={loading}>
          Search
        </Button>
        <Button className={classes.filterButton} variant="outlined" onClick={clear}>Clear</Button>
      </div>

      {loading ? (
        <div className={classes.loadingContainer}><CircularProgress size={40} /></div>
      ) : (
        <DataTable
          columns={COLUMNS}
          isEmpty={rows.length === 0}
          empty="No transactions match these filters."
          footer={<ServerPagination page={page} total={total} onChange={fetchRows} />}
        >
          {rows.map((t) => (
            <TableRow
              key={t.id}
              hover
              className={classes.clickableRow}
              onClick={() => history.push(`/clo/transactions/${t.id}`)}
            >
              <TableCell>{t.id}</TableCell>
              <TableCell>{fmtDateTime(t.transaction_at)}</TableCell>
              <TableCell>{t.event_type}</TableCell>
              <TableCell><StatusChip status={t.status} /></TableCell>
              <TableCell>{t.reason || "—"}</TableCell>
              <TableCell>{t.merchant_name || "—"}</TableCell>
              <TableCell align="right">{gbp(t.amount_pence)}</TableCell>
              <TableCell>{brandLabel(names, t.brand_id)}</TableCell>
              <TableCell>{t.user_id ?? "—"}</TableCell>
              <TableCell>{t.arn || "—"}</TableCell>
            </TableRow>
          ))}
        </DataTable>
      )}
      <Notification />
    </div>
  );
};

export default TransactionsList;
